import React, { useMemo, useContext } from 'react';
import { merge, cloneDeep, set } from 'lodash-es';
import { useSelector } from 'react-redux';
import { RapydThemeContext, RapydTheme } from '@rapyd/react-ui-library';
import { ThemeProvider } from 'styled-components';

import {
  getCustomStyles,
  getMerchantColor,
  isMobileSelector,
  isSdkSelector
} from 'redux/selectors/global';
import { useQueryParams } from 'hooks';
import themeOverrideObject, { pathsToOverride, defaultTheme } from 'theme';
import {
  getButtonColor,
  getMerchantDataByPageToken,
  removeEmpty
} from 'utilities/global';

const Theme = ({ children }) => {
  const merchantColor = useSelector(getMerchantColor);
  const customTheme = useSelector(getCustomStyles);
  const isSdk = useSelector(isSdkSelector);
  const isMobile = useSelector(isMobileSelector);

  const { token } = useQueryParams() || {};
  const merchantData = getMerchantDataByPageToken(token);
  const { color } = merchantData || {};
  const merchantColorToUse = merchantColor || getButtonColor(color);

  const themeOverride = useMemo(() => {
    if (!merchantColorToUse) return themeOverrideObject;

    const clonedTheme = cloneDeep(themeOverrideObject);
    pathsToOverride.forEach(path => {
      set(clonedTheme, path, merchantColorToUse);
    });

    return clonedTheme;
  }, [themeOverrideObject, pathsToOverride, merchantColorToUse]);

  const hcpTheme = useMemo(() => {
    const hcpTheme = merge(defaultTheme, { ...removeEmpty(customTheme) });
    const updatedFonts = {
      size: { ...(customTheme?.global?.fontSizes || {}) }
    };

    return {
      fonts: merge({}, themeOverride?.fonts || {}, updatedFonts),
      hcp: hcpTheme,
      view: {
        isMobile,
        isSdk,
        isMobileNotSdk: isMobile && !isSdk
      }
    };
  }, [defaultTheme, themeOverride, customTheme, isSdk, isMobile]);

  const mergedTheme = useMemo(() => {
    const merged = merge({}, themeOverride, hcpTheme);
    return merged;
  }, [themeOverride, hcpTheme]);

  return (
    <RapydTheme themeOverrides={mergedTheme}>
      <CustomTheme>{children}</CustomTheme>
    </RapydTheme>
  );
};

const CustomTheme = ({ children }) => {
  const rapydTheme = useContext(RapydThemeContext);

  return <ThemeProvider theme={rapydTheme}>{children}</ThemeProvider>;
};

export default Theme;
export const ThemeContext = RapydThemeContext;
